"use client";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, ExternalLink } from "lucide-react";
import { getContractExplorerUrl } from "@/lib/hoops-contracts";
import { cn } from "@/lib/utils";
import type { AccountBalances } from "./types";

interface BalancePanelProps {
  accountId: string;
  balances: AccountBalances;
  onRefresh: () => void;
}

const TOKENS = [
  { label: "XLM", key: "xlm" as const, color: "text-blue-400" },
  { label: "USDC", key: "usdc" as const, color: "text-green-400" },
  { label: "LP (Soroswap)", key: "lpSoroswap" as const, color: "text-purple-400" },
  { label: "LP (Aqua)", key: "lpAqua" as const, color: "text-cyan-400" },
];

export function BalancePanel({ accountId, balances, onRefresh }: BalancePanelProps) {
  if (!accountId) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Deploy or connect a Smart Account to see balances
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Smart Account</CardTitle>
          <Badge variant="outline" className="text-xs">TESTNET</Badge>
        </div>
        <a
          href={getContractExplorerUrl(accountId)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-mono text-xs text-blue-500 hover:underline"
        >
          {accountId.slice(0, 8)}...{accountId.slice(-6)}
          <ExternalLink className="w-3 h-3" />
        </a>
      </CardHeader>
      <CardContent className="space-y-3">
        {TOKENS.map(({ label, key, color }) => (
          <div key={key} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={cn("w-2 h-2 rounded-full", color.replace("text-", "bg-"))} />
              <span className="text-sm text-muted-foreground">{label}</span>
            </div>
            <span
              className={cn(
                "font-mono text-sm font-medium transition-colors duration-500",
                balances.isLoading && "text-muted-foreground"
              )}
            >
              {balances[key]}
            </span>
          </div>
        ))}

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <span className="text-xs text-muted-foreground">
            {balances.lastUpdated
              ? `Updated ${balances.lastUpdated.toLocaleTimeString()}`
              : "Not yet loaded"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={balances.isLoading}
            className="h-7 px-2"
          >
            <RefreshCw
              className={cn("w-3.5 h-3.5", balances.isLoading && "animate-spin")}
            />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
